import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { usePageTitle } from "@/components/common/AppShell";
import WizardShell from "@/components/common/WizardShell";
import Button from "@/components/common/Button";
import { useWizard } from "@/hooks/useWizard";
import { useFieldOptionConfig } from "@/constants/segmentOptions";
import {
  definitionListToEditor,
  mapSegmentToForm,
  prepareDefinition,
} from "@/components/segments/conditions/segmentFormUtils";
import Step1Define from "@/components/segments/wizard/Step1Define";
import Step3Review from "@/components/segments/wizard/Step3Review";
import AudienceSummarySidebar from "@/components/segments/wizard/AudienceSummarySidebar";

const STEPS = [
  { id: "define", label: "Define" },
  { id: "review", label: "Review & settings" },
];

const VAST_COUNT_DEBOUNCE_MS = 5000;

export default function SegmentBuilderPage() {
  const navigate = useNavigate();
  const params = useParams();
  const editingUuid = params.uuid ?? null;
  const {
    segments,
    addSegment,
    updateSegment,
    generateSegmentDefinition,
    previewSegmentCount,
    estimateSegmentReach,
    getSegmentStatistics,
    currentUser,
    performRequest,
  } = useApp();

  const editingSegment = useMemo(
    () => (editingUuid ? segments.find((s) => s.uuid === editingUuid) : null),
    [segments, editingUuid],
  );

  const isEdit = Boolean(editingUuid);
  usePageTitle(isEdit ? "Edit audience" : "Create audience");

  const [providers, setProviders] = useState([]);

  useEffect(() => {
    performRequest("/providers")
      .then((data) => setProviders(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Failed to load providers", err));
  }, [performRequest]);

  const providerOptions = useMemo(
    () =>
      providers
        .map((p) => ({ value: p.uuid, label: p.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [providers],
  );

  const fieldOptionConfig = useFieldOptionConfig(providerOptions);
  const [form, setForm] = useState(() => mapSegmentToForm(editingSegment));
  const [isSaving, setIsSaving] = useState(false);
  const [aiSummary, setAiSummary] = useState("");
  const [aiError, setAiError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [vastCount, setVastCount] = useState(null);
  const [vastCountLoading, setVastCountLoading] = useState(false);
  const [chEstimate, setChEstimate] = useState(null);
  const [chEstimateLoading, setChEstimateLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const previewAbortRef = useRef(null);
  const previewTimerRef = useRef(null);
  const isFirstPreviewRef = useRef(true);
  const skipPreviewWhileSavingRef = useRef(false);

  const isStepValid = useCallback(
    (index) => {
      if (index === 0) return form.name.trim().length > 0;
      return true;
    },
    [form.name],
  );

  const wizard = useWizard({ stepCount: STEPS.length, isStepValid });

  const goBack = useCallback(() => navigate("/dashboard/segments"), [navigate]);

  useEffect(() => {
    isFirstPreviewRef.current = true;
    setForm(mapSegmentToForm(editingSegment));
    setAiSummary("");
    setAiError(null);
    setIsGenerating(false);
    setVastCount(null);
    setChEstimate(null);
  }, [editingSegment]);

  useEffect(() => {
    if (!editingSegment?.uuid) {
      setStats(null);
      return undefined;
    }
    let cancelled = false;
    setStatsLoading(true);
    getSegmentStatistics(editingSegment.uuid)
      .then((result) => {
        if (!cancelled) setStats(result);
      })
      .catch((error) => {
        console.error("Failed to load segment statistics", error);
        if (!cancelled) setStats(null);
      })
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [editingSegment?.uuid, getSegmentStatistics]);

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateDefinition = (group, updater) => {
    setForm((prev) => ({
      ...prev,
      definition: {
        ...prev.definition,
        [group]: updater(prev.definition[group] || []),
      },
    }));
  };

  // Replace an entire definition bucket (all | any | none). The category-based
  // BuildConditions builder drives everything through this single setter.
  const setGroup = (group, list) => {
    updateDefinition(group, () => list);
  };

  const handleNumberChange = (field, value) => {
    if (value === "") {
      updateField(field, "");
      return;
    }
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return;

    updateField(field, numericValue);
  };

  const applyGeneratedDefinition = (definition = {}) => {
    setForm((prev) => ({
      ...prev,
      definition: {
        all: definitionListToEditor(definition.all),
        any: definitionListToEditor(definition.any),
        none: definitionListToEditor(definition.none),
      },
    }));
  };

  const handleGenerateDefinition = async () => {
    const trimmedPrompt = (form.description ?? "").trim();
    if (!trimmedPrompt) {
      setAiError("Describe your target audience before sending.");
      return;
    }

    setIsGenerating(true);
    setAiError(null);

    try {
      const result = await generateSegmentDefinition(trimmedPrompt);
      applyGeneratedDefinition(result.data || {});
      setAiSummary("Filters were updated based on your description.");
    } catch (error) {
      console.error("Failed to generate segment definition", error);
      setAiError("Unable to generate filters. Try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const cancelPreviewCount = useCallback(() => {
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }

    if (previewAbortRef.current) {
      previewAbortRef.current.abort();
      previewAbortRef.current = null;
    }
  }, []);

  const calculateVastCount = useCallback(async () => {
    if (skipPreviewWhileSavingRef.current) return;

    const definition = prepareDefinition(form.definition);

    if (previewAbortRef.current) {
      previewAbortRef.current.abort();
    }

    const controller = new AbortController();
    previewAbortRef.current = controller;

    setVastCountLoading(true);
    setChEstimateLoading(true);

    // PG live preview count and the ClickHouse migrated-week reach estimate run
    // in parallel; the CH estimate degrades to null (no migrated data / CH down)
    // without breaking the live count.
    const countPromise = previewSegmentCount(definition, form.kind, {
      signal: controller.signal,
    })
      .then((count) => setVastCount(count))
      .catch((error) => {
        if (error?.name === "AbortError") return;
        console.error("Failed to calculate Unique Impressions count", error);
        setVastCount(null);
      });

    const estimatePromise = estimateSegmentReach(definition, {
      signal: controller.signal,
    })
      .then((estimate) => setChEstimate(estimate))
      .catch((error) => {
        if (error?.name === "AbortError") return;
        setChEstimate(null);
      });

    try {
      await Promise.all([countPromise, estimatePromise]);
    } finally {
      if (previewAbortRef.current === controller) {
        previewAbortRef.current = null;
        setVastCountLoading(false);
        setChEstimateLoading(false);
      }
    }
  }, [form.definition, form.kind, previewSegmentCount, estimateSegmentReach]);

  useEffect(() => {
    cancelPreviewCount();

    if (isFirstPreviewRef.current) {
      isFirstPreviewRef.current = false;
      calculateVastCount();
      return undefined;
    }

    previewTimerRef.current = setTimeout(() => {
      calculateVastCount();
    }, VAST_COUNT_DEBOUNCE_MS);

    return () => {
      cancelPreviewCount();
    };
  }, [form.definition, form.kind, calculateVastCount, cancelPreviewCount]);

  const handleSubmit = async () => {
    cancelPreviewCount();
    skipPreviewWhileSavingRef.current = true;

    setIsSaving(true);
    try {
      const definition = prepareDefinition(form.definition);

      const payload = { ...form, definition };

      if (editingSegment) {
        await updateSegment(editingSegment.uuid, {
          ...payload,
          updatedAt: new Date().toISOString(),
        });
      } else {
        await addSegment({
          ...payload,
          createdBy: currentUser || "admin",
        });
      }
      goBack();
    } catch (error) {
      console.error("Failed to save segment", error);
      if (typeof window !== "undefined") {
        window.alert("Failed to save segment. Please try again.");
      }
    } finally {
      setIsSaving(false);
      skipPreviewWhileSavingRef.current = false;
    }
  };

  const nextDisabled = !isStepValid(wizard.currentStep);

  const footer = (
    <>
      <Button
        variant="secondary"
        onClick={wizard.isFirstStep ? goBack : wizard.goBack}
        disabled={isSaving}
      >
        {wizard.isFirstStep ? "Cancel" : "Back"}
      </Button>
      {wizard.isLastStep ? (
        <Button onClick={handleSubmit} disabled={isSaving}>
          {isSaving
            ? isEdit
              ? "Saving…"
              : "Creating…"
            : isEdit
              ? "Save audience"
              : "Create audience"}
        </Button>
      ) : (
        <Button onClick={wizard.goNext} disabled={nextDisabled}>
          Next: {STEPS[wizard.currentStep + 1].label}
        </Button>
      )}
    </>
  );

  return (
    <WizardShell
      backLabel="Back to audiences"
      onBack={goBack}
      steps={STEPS}
      currentStep={wizard.currentStep}
      onStepChange={wizard.goToStep}
      canGoToStep={wizard.canGoToStep}
      summary={
        <AudienceSummarySidebar
          form={form}
          author={currentUser}
          fieldOptionConfig={fieldOptionConfig}
          vastCount={vastCount}
          vastCountLoading={vastCountLoading}
          chEstimate={chEstimate}
          chEstimateLoading={chEstimateLoading}
          stats={stats}
          statsLoading={statsLoading}
        />
      }
      footer={footer}
    >
      {wizard.currentStep === 0 && (
        <Step1Define
          form={form}
          updateField={updateField}
          onGenerate={handleGenerateDefinition}
          isGenerating={isGenerating}
          aiError={aiError}
          aiSummary={aiSummary}
          definition={form.definition}
          onSetGroup={setGroup}
          fieldOptionConfig={fieldOptionConfig}
        />
      )}
      {wizard.currentStep === 1 && (
        <Step3Review
          form={form}
          updateField={updateField}
          onNumberChange={handleNumberChange}
          fieldOptionConfig={fieldOptionConfig}
        />
      )}
    </WizardShell>
  );
}
